import React, { useState } from "react";
import hosys from "@/api/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Pencil, Trash2, Plus, Download, Loader2 } from "lucide-react";
import moment from "moment";

const DOC_TYPES = { risk_assessment: "Avaluació de riscos", prevention_plan: "Pla de prevenció", safety_protocol: "Protocol de seguretat", emergency_plan: "Pla d'emergència", other: "Altres" };
const STATUSES = { draft: "Esborrany", approved: "Aprovat", in_force: "En vigor", obsolete: "Obsolet" };

export default function SafetyDocumentManager({ venue_id, documents, onChanged, isAdmin }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ document_type: "safety_protocol", title: "", description: "", issue_date: new Date().toISOString().split("T")[0], status: "draft", version: 1 });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const openForm = (item = null) => {
    setEditing(item);
    setForm(item ? { ...form, ...item } : { document_type: "safety_protocol", title: "", description: "", issue_date: new Date().toISOString().split("T")[0], status: "draft", version: 1, venue_id });
    setShowForm(true);
  };

  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { file_url } = await hosys.integrations.Core.UploadFile({ file });
      set("file_url", file_url);
    } catch (err) {
      console.error("Error uploading:", err);
    }
    setUploading(false);
  };

  const handleSave = async () => {
    if (!form.title) return;
    setSaving(true);
    if (editing?.id) {
      await hosys.entities.SafetyDocument.update(editing.id, { ...form, venue_id });
    } else {
      await hosys.entities.SafetyDocument.create({ ...form, venue_id });
    }
    setSaving(false);
    setShowForm(false);
    onChanged();
  };

  const handleDelete = async (id) => {
    await hosys.entities.SafetyDocument.delete(id);
    onChanged();
  };

  const statusColor = { draft: "bg-gray-100 text-gray-700", approved: "bg-blue-100 text-blue-700", in_force: "bg-emerald-100 text-emerald-700", obsolete: "bg-red-100 text-red-700" };

  return (
    <div className="space-y-3">
      {isAdmin && (
        <Button onClick={() => openForm()} className="bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-2">
          <Plus className="w-4 h-4" /> Afegir document
        </Button>
      )}

      {documents.length === 0 ? (
        <Card className="p-8 text-center border-0 shadow-sm"><p className="text-gray-500">Cap document pujat</p></Card>
      ) : (
        <div className="space-y-2">
          {documents.map(d => (
            <Card key={d.id} className="p-4 border-0 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h4 className="font-semibold text-gray-900">{d.title}</h4>
                    <Badge className="bg-blue-100 text-blue-700 border-0 text-[10px]">{DOC_TYPES[d.document_type]}</Badge>
                    <Badge className={`border-0 text-[10px] ${statusColor[d.status]}`}>{STATUSES[d.status]}</Badge>
                    <span className="text-xs text-gray-400">v{d.version || 1}</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-1">{d.description}</p>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span>Publicat: {moment(d.issue_date).format("DD/MM/YYYY")}</span>
                    {d.expiry_date && <span>Caduca: {moment(d.expiry_date).format("DD/MM/YYYY")}</span>}
                    {d.responsible_name && <span>Responsable: {d.responsible_name}</span>}
                  </div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  {d.file_url && (
                    <a href={d.file_url} target="_blank" rel="noopener noreferrer">
                      <Button variant="ghost" size="icon" className="w-7 h-7 text-gray-400 hover:text-blue-600"><Download className="w-3.5 h-3.5" /></Button>
                    </a>
                  )}
                  {isAdmin && (
                    <>
                      <Button variant="ghost" size="icon" className="w-7 h-7 text-gray-400 hover:text-[#7B2D3F]" onClick={() => openForm(d)}><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="w-7 h-7 text-gray-400 hover:text-red-600" onClick={() => handleDelete(d.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={o => !o && setShowForm(false)}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Editar document" : "Afegir document"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Tipus *</Label><Select value={form.document_type} onValueChange={v => set("document_type", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{Object.entries(DOC_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Títol *</Label><Input value={form.title} onChange={e => set("title", e.target.value)} placeholder="Títol del document" /></div>
            <div><Label>Descripció</Label><Textarea value={form.description} onChange={e => set("description", e.target.value)} rows={2} placeholder="Descripció..." /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Data publicació</Label><Input type="date" value={form.issue_date} onChange={e => set("issue_date", e.target.value)} /></div>
              <div><Label>Data caducitat</Label><Input type="date" value={form.expiry_date || ""} onChange={e => set("expiry_date", e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Versió</Label><Input type="number" value={form.version || 1} onChange={e => set("version", parseInt(e.target.value) || 1)} min="1" /></div>
              <div><Label>Estat</Label><Select value={form.status} onValueChange={v => set("status", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{Object.entries(STATUSES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div><Label>Responsable</Label><Input value={form.responsible_name || ""} onChange={e => set("responsible_name", e.target.value)} placeholder="Nom" /></div>
            <div><Label>Document (PDF/Word)</Label><input type="file" accept=".pdf,.doc,.docx" onChange={handleFileSelect} disabled={uploading} className="block w-full text-sm text-gray-500" />{uploading && <p className="text-xs text-blue-600 mt-1">Pujant...</p>}</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel·lar</Button>
            <Button onClick={handleSave} disabled={!form.title || saving} className="bg-[#7B2D3F] hover:bg-[#5B1D2F]">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{editing ? "Actualitzar" : "Afegir"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}